<template>
  <div class="job-status">
    <div class="header">
      <h1>Job Status</h1>
      <div class="controls">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search Job ID, Order No, Description..."
          class="search-input"
          :disabled="needsCustomerSelection"
        />
        <select
          v-if="isAdminOrManager"
          v-model="selectedCustomer"
          class="customer-select"
        >
          <option value="">Select customer...</option>
          <option v-for="customer in customers" :key="customer" :value="customer">
            {{ customer }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="needsCustomerSelection" class="loading">
      Select a customer to view jobs.
    </div>
    <div v-else-if="isLoading" class="loading">Loading jobs...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th class="sortable" @click="setSort('JobID')">
                Job ID <span class="sort-ind">{{ sortIndicator('JobID') }}</span>
              </th>
              <th class="sortable" @click="setSort('OrderNo')">
                Order No <span class="sort-ind">{{ sortIndicator('OrderNo') }}</span>
              </th>
              <th class="sortable" @click="setSort('KitDescription')">
                Kit Description <span class="sort-ind">{{ sortIndicator('KitDescription') }}</span>
              </th>
              <th class="sortable" @click="setSort('DueDate')">
                Due Date <span class="sort-ind">{{ sortIndicator('DueDate') }}</span>
              </th>
              <th class="sortable" @click="setSort('OrderDate')">
                Order Date <span class="sort-ind">{{ sortIndicator('OrderDate') }}</span>
              </th>
              <th class="sortable" @click="setSort('Qty')">
                Kit Qty <span class="sort-ind">{{ sortIndicator('Qty') }}</span>
              </th>
              <th class="sortable" @click="setSort('State')">
                State <span class="sort-ind">{{ sortIndicator('State') }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="jobs.length === 0">
              <td colspan="7" class="empty-row">No jobs found.</td>
            </tr>
            <tr v-for="job in jobs" :key="job.JobID">
              <td>{{ job.JobID }}</td>
              <td>{{ job.OrderNo || '-' }}</td>
              <td>{{ job.KitDescription || '-' }}</td>
              <td>{{ formatDate(job.DueDate) }}</td>
              <td>{{ formatDate(job.OrderDate) }}</td>
              <td>{{ job.Qty }}</td>
              <td>
                <span
                  v-if="job.State"
                  :class="['state-badge', stateClass(job.State)]"
                >{{ job.State }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-container" v-if="pagination.totalPages > 1">
        <div class="pagination-info">
          <span>
            Showing
            {{ Math.max(1, pagination.itemsPerPage * (pagination.currentPage - 1) + 1) }}
            to
            {{ Math.min(pagination.itemsPerPage * pagination.currentPage, pagination.totalItems) }}
            of {{ pagination.totalItems }} jobs
          </span>
        </div>
        <div class="pagination-controls">
          <button
            class="pagination-btn"
            :disabled="!pagination.hasPreviousPage"
            @click="goToPage(1)"
          >
            First
          </button>
          <button
            class="pagination-btn"
            :disabled="!pagination.hasPreviousPage"
            @click="goToPage(pagination.currentPage - 1)"
          >
            Previous
          </button>
          <div class="page-numbers">
            <button
              v-for="page in visiblePages"
              :key="page"
              class="pagination-btn page-number"
              :class="{ active: page === pagination.currentPage }"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
          </div>
          <button
            class="pagination-btn"
            :disabled="!pagination.hasNextPage"
            @click="goToPage(pagination.currentPage + 1)"
          >
            Next
          </button>
          <button
            class="pagination-btn"
            :disabled="!pagination.hasNextPage"
            @click="goToPage(pagination.totalPages)"
          >
            Last
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'JobStatusView',
  data() {
    return {
      jobs: [],
      searchQuery: '',
      selectedCustomer: '',
      customers: [],
      isLoading: false,
      error: null,
      userRole: '',
      searchTimeout: null,
      sortBy: 'JobID',
      sortDir: 'desc',
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 25,
        hasNextPage: false,
        hasPreviousPage: false
      },
      itemsPerPage: 25
    };
  },
  computed: {
    isAdminOrManager() {
      return this.userRole === 'Admin' || this.userRole === 'Manager';
    },
    needsCustomerSelection() {
      return this.isAdminOrManager && !this.selectedCustomer;
    },
    visiblePages() {
      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const delta = 2;
      let start = Math.max(1, current - delta);
      let end = Math.min(total, current + delta);
      if (current <= delta) end = Math.min(total, 2 * delta + 1);
      if (current >= total - delta) start = Math.max(1, total - 2 * delta);
      const pages = [];
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    searchQuery() {
      this.debounceSearch();
    },
    selectedCustomer() {
      this.pagination.currentPage = 1;
      this.jobs = [];
      if (this.needsCustomerSelection) {
        this.error = null;
        this.isLoading = false;
        return;
      }
      this.fetchJobs();
    }
  },
  methods: {
    debounceSearch() {
      if (this.needsCustomerSelection) return;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.currentPage = 1;
        this.fetchJobs();
      }, 300);
    },
    async loadUser() {
      const response = await axios.get('/auth/check-auth');
      this.userRole = response.data.user?.role || '';
    },
    async fetchCustomers() {
      if (!this.isAdminOrManager) return;
      try {
        const response = await axios.get('/customers');
        this.customers = response.data.data || [];
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    },
    async fetchJobs() {
      if (this.needsCustomerSelection) {
        this.jobs = [];
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.error = null;
      try {
        const params = {
          page: this.pagination.currentPage,
          limit: this.itemsPerPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir
        };
        if (this.searchQuery.trim()) {
          params.searchTerm = this.searchQuery.trim();
        }
        if (this.isAdminOrManager) {
          params.customer = this.selectedCustomer;
        }
        const response = await axios.get('/jobs/status', { params });
        this.jobs = response.data.data || [];
        if (response.data.pagination) {
          this.pagination = { ...this.pagination, ...response.data.pagination };
        }
      } catch (error) {
        console.error('Error fetching job status:', error);
        this.error = error.response?.data?.message || 'Failed to load job status';
        this.jobs = [];
      } finally {
        this.isLoading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination.currentPage = page;
      this.fetchJobs();
    },
    setSort(column) {
      if (this.needsCustomerSelection) return;
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        this.sortDir = column === 'JobID' ? 'desc' : 'asc';
      }
      this.pagination.currentPage = 1;
      this.fetchJobs();
    },
    sortIndicator(column) {
      if (this.sortBy !== column) return '';
      return this.sortDir === 'asc' ? '▲' : '▼';
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString();
    },
    stateClass(state) {
      // Colours from VisionJobManager FrmJobManager.cs
      const map = {
        Completed: 'state-completed',
        'On Hold': 'state-onhold',
        'Wait Stock': 'state-waitstock',
        Crimped: 'state-crimped',
        Cut: 'state-cut',
        Started: 'state-started'
      };
      return map[state] || '';
    }
  },
  async mounted() {
    try {
      await this.loadUser();
      if (this.isAdminOrManager) {
        await this.fetchCustomers();
        this.isLoading = false;
      } else {
        await this.fetchJobs();
      }
    } catch (error) {
      console.error('Failed to initialize Job Status:', error);
      this.error = 'Failed to initialize page';
      this.isLoading = false;
    }
  }
};
</script>

<style scoped>
.job-status {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.controls {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.search-input,
.customer-select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.search-input {
  min-width: 260px;
}

.loading,
.error {
  text-align: center;
  padding: 20px;
}

.error {
  color: red;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.data-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}

.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.sortable:hover {
  background-color: #ebebeb;
}

.sort-ind {
  font-size: 0.7rem;
  color: #555;
}

.empty-row {
  text-align: center;
  color: #666;
}

.state-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #222;
}

/* Match VisionJobManager FrmJobManager.cs colours */
.state-onhold {
  background-color: #ADD8E6; /* Color.LightBlue */
}

.state-waitstock {
  background-color: #FFB2A4; /* ControlPaint.Light(Color.Tomato) */
}

.state-cut {
  background-color: #FFE4E1; /* Color.MistyRose */
}

.state-crimped {
  background-color: #90EE90; /* Color.LightGreen */
}

.state-started {
  background-color: #FFFACD; /* Color.LemonChiffon */
}

.state-completed {
  background-color: #D3D3D3; /* LightGray */
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-controls {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.pagination-btn {
  padding: 6px 10px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: #333;
  color: #fff;
  border-color: #333;
}
</style>
